// FolderPrefsDialog — modal dialog for customizing the EMAIL
// section's folder list.
//
// Two controls per folder:
//   * Visibility checkbox (hide from sidebar)
//   * Drag handle to reorder
//
// Layout:
//   * Header: "Customize folders"
//   * Body: scrollable list of folder rows
//   * Footer: Reset to default + Done buttons
//
// Drag-and-drop is plain HTML5 (no library). Drop target is the
// `<li>` boundaries; we move the dragged item just before the
// drop target's index. Keyboard reorder via the up/down icons
// alongside the drag handle for accessibility.

import { ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useMemo } from "react";

import type { EmailMailFolder } from "../../api";
import { Button } from "../../ui";
import { applyPrefsToFolderIds, useFolderPrefs } from "../../utils/email-folder-prefs";
import { Modal } from "../Modal";

export interface FolderPrefsDialogProps {
  open: boolean;
  onClose: () => void;
  // Flat list of folder rows (in natural order — typically the
  // system-folder-first sort that EmailSidebarSection already
  // computes). The dialog adds visibility + reorder on top.
  folders: ReadonlyArray<EmailMailFolder>;
}

function folderKey(folder: EmailMailFolder): string {
  return folder.wellKnownName ?? folder.id;
}

export function FolderPrefsDialog({ open, onClose, folders }: FolderPrefsDialogProps) {
  // Prefs live in the account-level settings provider (2026-08-15), so
  // the sidebar behind this dialog re-renders from the same source —
  // no onPrefsChanged callback needed to keep the two in step.
  const { prefs, setHidden, setOrder, reset } = useFolderPrefs();

  const naturalIds = useMemo(() => folders.map((f) => folderKey(f)), [folders]);
  const orderedIds = useMemo(() => {
    // Show ALL folders in the dialog (visible + hidden) so the user
    // can re-show what they hid. Hidden ones render dim.
    if (prefs.order.length === 0) return naturalIds;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of prefs.order) {
      if (naturalIds.includes(id) && !seen.has(id)) {
        out.push(id);
        seen.add(id);
      }
    }
    for (const id of naturalIds) {
      if (!seen.has(id)) out.push(id);
    }
    return out;
  }, [prefs.order, naturalIds]);

  const folderById = useMemo(() => {
    const map = new Map<string, EmailMailFolder>();
    for (const f of folders) map.set(folderKey(f), f);
    return map;
  }, [folders]);

  const toggleHidden = useCallback(
    (id: string) => {
      setHidden(id, !prefs.hidden.has(id));
    },
    [prefs.hidden, setHidden],
  );

  const moveBy = useCallback(
    (id: string, delta: number) => {
      const baseOrder = orderedIds.slice();
      const fromIdx = baseOrder.indexOf(id);
      if (fromIdx < 0) return;
      const toIdx = Math.max(0, Math.min(baseOrder.length - 1, fromIdx + delta));
      if (toIdx === fromIdx) return;
      baseOrder.splice(fromIdx, 1);
      baseOrder.splice(toIdx, 0, id);
      setOrder(baseOrder);
    },
    [orderedIds, setOrder],
  );

  const reorderViaDrag = useCallback(
    (sourceId: string, targetId: string) => {
      if (sourceId === targetId) return;
      const baseOrder = orderedIds.slice();
      const fromIdx = baseOrder.indexOf(sourceId);
      const toIdx = baseOrder.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      baseOrder.splice(fromIdx, 1);
      baseOrder.splice(toIdx, 0, sourceId);
      setOrder(baseOrder);
    },
    [orderedIds, setOrder],
  );

  // Preview shown at top: the visible folders, in order, after the
  // current prefs apply. Matches what'll show in the sidebar.
  const previewIds = useMemo(() => applyPrefsToFolderIds(naturalIds, prefs), [naturalIds, prefs]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Customize folders"
      className="modal-folder-prefs"
      footer={
        <>
          <Button variant="ghost" onClick={reset}>
            Reset to default
          </Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <p className="folder-prefs-hint">
        Drag to reorder. Uncheck a folder to hide it from the sidebar.
      </p>

      <ul className="folder-prefs-list">
        {orderedIds.map((id) => {
          const folder = folderById.get(id);
          if (!folder) return null;
          const isHidden = prefs.hidden.has(id);
          return (
            <li
              key={id}
              className={`folder-prefs-row${isHidden ? " is-hidden" : ""}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", id);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceId = e.dataTransfer.getData("text/plain");
                if (sourceId) reorderViaDrag(sourceId, id);
              }}
            >
              <span className="folder-prefs-handle" aria-hidden>
                ⋮⋮
              </span>
              <input
                type="checkbox"
                className="folder-prefs-checkbox"
                checked={!isHidden}
                onChange={() => toggleHidden(id)}
                aria-label={`${isHidden ? "Show" : "Hide"} ${folder.displayName}`}
              />
              <span className="folder-prefs-name">{folder.displayName}</span>
              {folder.unreadItemCount > 0 ? (
                <span className="folder-prefs-unread">
                  {folder.unreadItemCount > 999 ? "999+" : folder.unreadItemCount}
                </span>
              ) : null}
              <div className="folder-prefs-arrows">
                <button
                  type="button"
                  onClick={() => moveBy(id, -1)}
                  aria-label="Move up"
                  title="Move up"
                >
                  <ChevronUp size={14} strokeWidth={1.5} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => moveBy(id, 1)}
                  aria-label="Move down"
                  title="Move down"
                >
                  <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <details className="folder-prefs-preview">
        <summary>Preview ({previewIds.length} visible)</summary>
        <ol>
          {previewIds.map((id) => (
            <li key={`prev-${id}`}>{folderById.get(id)?.displayName ?? id}</li>
          ))}
        </ol>
      </details>
    </Modal>
  );
}
